import '@/styles/globals.css';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppProps } from 'next/app';
import { Montserrat, Work_Sans } from 'next/font/google';
import Head from 'next/head';
import Providers from '@common/Providers';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';

import localFont from 'next/font/local';
import { useRouter } from 'next/router';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { cx } from '@/utils';
import { Analytics } from '@vercel/analytics/next';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import Cookies from 'js-cookie';
import NextNProgress from 'nextjs-progressbar';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Toaster } from 'react-hot-toast';
import { ToastContainer } from 'react-toastify';

import { useActivePageStore, useSelectedPageStore } from '@/utils/store/store';
import AdminLayout from '@/components/common/Layout/AdminLayout/AdminLayout';
import { Theme } from '@/components/common/Providers/GraphqlProvider/generated';
import { Splash } from '@/components/common/Splash/Splash';

// import { TailwindIndicator } from '@/components/common/TailwindIndicator';

import 'dayjs/locale/es';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { MdKeyboardArrowUp } from 'react-icons/md';

import { ConfirmDialogProvider } from '@/components/common/ConfirmDialogProvider';
import { CookieConsentModal } from '@/components/common/CookieConsentModal/CookieConsentModal';
import { NetworkStatusBanner } from '@/components/common/NetworkStatusBanner';
import { PreviewPageIndicator } from '@/components/common/PreviewPageIndicator';

dayjs.extend(localizedFormat);

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('America/Bogota');
dayjs.locale('es');
dayjs.extend(relativeTime);

type NextPageWithLayout = { getLayout?: (page: ReactNode) => ReactNode };

type AppPropsWithLayout = AppProps & { Component: NextPageWithLayout };

const montserrat = Montserrat({
  weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
  subsets: ['latin'],
  style: ['normal', 'italic'],
  variable: '--font-montserrat',
  display: 'swap',
  fallback: ['system-ui', 'arial', 'sans-serif'],
  preload: true,
});

const workSans = Work_Sans({
  weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
  subsets: ['latin'],
  style: ['normal', 'italic'],
  variable: '--font-workSans',
  display: 'swap',
  fallback: ['system-ui', 'arial', 'sans-serif'],
  preload: true,
});

const dislexic = localFont({
  src: '../../public/fonts/OpenDyslexicMono-Regular.otf',
  variable: '--font-dislexic',
});

export const queryClient = new QueryClient();

// Helper functions moved outside component to prevent re-creation
const isMainAdminPage = (path: string) => {
  return (
    path === '/admin' ||
    path === '/admin/home' ||
    (path.startsWith('/admin/home/') && path.split('/').length <= 3) //just 3 levels deep
  );
};

const isDeepAdminPage = (path: string) => {
  return path.startsWith('/admin/') && !isMainAdminPage(path);
};

const isAdminPage = (path: string) => {
  return isMainAdminPage(path) || isDeepAdminPage(path);
};

// Conditional wrapper for reCAPTCHA - only applies to public pages and admin login
const ReCaptchaWrapper = ({
  children,
  isAdmin,
  pathname,
}: {
  children: ReactNode;
  isAdmin: boolean;
  pathname: string;
}) => {
  // If it's an admin page BUT NOT the login page (/admin), skip reCAPTCHA
  if (isAdmin && pathname !== '/admin') {
    return <>{children}</>;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_V3!}
      scriptProps={{ async: true, defer: true }}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
  const router = useRouter();
  const { theme: themeDraft } = useSelectedPageStore();
  const { theme } = useActivePageStore();
  const [activeTheme, setActiveTheme] = useState('marine__theme');
  const [splashLoading, setSplashLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);

  // Hook para detectar navegación por teclado
  useKeyboardNavigation();

  // Memoize admin page detection to prevent unnecessary re-renders
  const isCurrentPageAdmin = useMemo(() => isAdminPage(router.pathname), [router.pathname]);

  useEffect(() => {
    console.log('Ver. 0.0.1');
    if (themeDraft && router.route.includes('/admin')) {
      setActiveTheme(getThemeClass(themeDraft));
    } else {
      const pageCache = Cookies.get('page');
      if (pageCache && router.route.includes('/admin')) {
        const jsonPageCache = JSON.parse(pageCache);
        setActiveTheme(getThemeClass(jsonPageCache.theme));
      } else if (theme) {
        setActiveTheme(getThemeClass(theme));
      }
    }
  }, [router.route, theme, themeDraft]);

  useEffect(() => {
    setTimeout(() => {
      setSplashLoading(false);
      setTimeout(() => {
        setShowSplash(false);
      }, 500);
    }, 800);
  }, []);

  useEffect(() => {
    document.body.style.overflow = splashLoading ? 'hidden' : 'auto';
  }, [splashLoading]);

  useEffect(() => {
    const body = document.body;
    body.classList.forEach((className) => {
      if (className.includes('__theme')) {
        body.classList.remove(className);
      }
    });
    body.classList.add(activeTheme);
  }, [activeTheme]);

  // Inicializar Google Translate globalmente
  useEffect(() => {
    if ((window as any).googleTranslateElementInit) return;

    (window as any).googleTranslateElementInit = function () {
      new (window as any).google.translate.TranslateElement(
        { pageLanguage: 'es', autoDisplay: false },
        'google_translate_element'
      );
    };

    const script = document.createElement('script');
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // Determine which layout to use
  const getLayoutWrapper = (page: ReactNode) => {
    if (isMainAdminPage(router.pathname)) {
      return <AdminLayout showSideMenu={false}>{page}</AdminLayout>;
    } else if (isDeepAdminPage(router.pathname) && !Component.getLayout) {
      return <AdminLayout showSideMenu={true}>{page}</AdminLayout>;
    }

    return (
      <>
        {page}
        <GoUpButton />
      </>
    );
  };

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page: ReactNode) => page);

  // SEO props from pages
  const seoTitle = pageProps.seoTitle;
  const seoDescription = pageProps.seoDescription;
  const seoImage = pageProps.seoImage;
  const canonical = pageProps.canonical;

  const DEFAULT_TITLE = 'Sede Electrónica';
  const DEFAULT_DESCRIPTION = 'Portal y servicios de la Sede Electrónica';

  const resolvedTitle = seoTitle
    ? seoTitle.includes(DEFAULT_TITLE)
      ? seoTitle
      : `${seoTitle} - ${DEFAULT_TITLE}`
    : DEFAULT_TITLE;

  const resolvedDescription = seoDescription || DEFAULT_DESCRIPTION;

  return (
    <>
      <Head>
        {/* Title */}
        <title key="title">{resolvedTitle}</title>
        <meta key="description" name="description" content={resolvedDescription} />

        {/* Open Graph */}
        <meta key="og:title" property="og:title" content={resolvedTitle} />
        <meta key="og:description" property="og:description" content={resolvedDescription} />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:site_name" property="og:site_name" content={DEFAULT_TITLE} />
        {canonical && <meta key="og:url" property="og:url" content={canonical} />}
        {seoImage && (
          <>
            <meta key="og:image" property="og:image" content={seoImage} />
            <meta key="og:image:secure_url" property="og:image:secure_url" content={seoImage} />
            <meta key="og:image:alt" property="og:image:alt" content={resolvedTitle} />
          </>
        )}

        {/* Twitter Card */}
        <meta
          key="twitter:card"
          name="twitter:card"
          content={seoImage ? 'summary_large_image' : 'summary'}
        />
        <meta key="twitter:title" name="twitter:title" content={resolvedTitle} />
        <meta key="twitter:description" name="twitter:description" content={resolvedDescription} />
        {seoImage && <meta key="twitter:image" name="twitter:image" content={seoImage} />}
      </Head>

      {/* Banner de estado de conexión a internet */}
      <NetworkStatusBanner position="top" showSlowConnection={true} />

      {showSplash && (
        <Splash className={cx({ 'opacity-100': splashLoading }, { 'opacity-0': !splashLoading })} />
      )}

      <ReCaptchaWrapper isAdmin={isCurrentPageAdmin} pathname={router.pathname}>
        <QueryClientProvider client={queryClient}>
          <ConfirmDialogProvider>
            <main className={`${montserrat.variable} ${workSans.variable} ${dislexic.variable}`}>
              <Toaster position="top-center" reverseOrder={false} />

              {/* Div oculto para Google Translate - global */}
              <div id="google_translate_element" style={{ display: 'none' }} />

              <ToastContainer hideProgressBar position="top-center" />
              {/* <TailwindIndicator /> */}
              <NextNProgress
                color={'white'}
                startPosition={0.3}
                stopDelayMs={200}
                height={6}
                showOnShallow={true}
              />
              <Providers {...pageProps}>
                {getLayoutWrapper(getLayout(<Component {...pageProps} />))}
                {!isCurrentPageAdmin && <CookieConsentModal />}
                <PreviewPageIndicator />
                <Analytics />
              </Providers>
            </main>
          </ConfirmDialogProvider>
        </QueryClientProvider>
      </ReCaptchaWrapper>
    </>
  );
};

const GoUpButton = () => {
  const [showGoUp, setShowGoUp] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const shouldShow = window.scrollY > 300;

      if (shouldShow && !showGoUp) {
        // Aparición: primero mostrar el elemento, luego animar
        setShowGoUp(true);
        setTimeout(() => setIsVisible(true), 10);
      } else if (!shouldShow && showGoUp) {
        // Desaparición: primero animar, luego ocultar el elemento
        setIsVisible(false);
        setTimeout(() => setShowGoUp(false), 300);
      }
    };

    // Agregar el event listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup del event listener
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showGoUp]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // No renderizar si no se debe mostrar
  if (!showGoUp) return null;

  return (
    <div
      className={`fixed right-2.5 top-[80%] flex size-14 h-14 transform cursor-pointer place-items-center justify-center rounded-full bg-primary shadow-lg transition-all duration-300 ${
        isVisible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-4 scale-75 opacity-0'
      }`}
      aria-label="Volver arriba"
      role="button"
      tabIndex={0}
      onClick={scrollToTop}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          scrollToTop();
        }
      }}
    >
      <div
        className={`flex min-h-9 min-w-9 place-items-center content-center items-center justify-center rounded-full bg-white`}
      >
        <MdKeyboardArrowUp className="size-9 text-primary" />
      </div>
    </div>
  );
};

// Helper function to get theme class
const getThemeClass = (theme: Theme): string => {
  switch (theme) {
    case Theme.GreenDark:
      return 'green__dark__theme';
    case Theme.Gold:
      return 'gold__theme';
    case Theme.ElfGreen:
      return 'elf__green__theme';
    case Theme.Orange:
      return 'orange__theme';
    case Theme.Shiraz:
      return 'shiraz__theme';
    case Theme.RadicalRed:
      return 'radical__red__theme';
    case Theme.BlueDark:
      return 'blue__dark__theme';
    default:
      return 'marine__theme';
  }
};

export default App;
